// ---------------------------------------------------------------------------
// Results export, in ";"-separated format. Two different exports, don't mix
// them up:
//   - generateSubBasinContributionsCsv: one column per SUB-BASIN, in mm/day
//     — this web app's own export (diagnostics/debugging), no equivalent in
//     the original.
//   - generateAggregatedContributionsCsv: one column per OUTLET POINT or
//     ZONE, already aggregated to volume — the web app's own simplified
//     take on the real "Sal_Aportaciones.csv" (see F.acumula_apo in
//     Evalhid.dll, src/engine/aggregation.ts): only the total contribution,
//     not the real file's 3-column Etot/Esup/Esubt breakdown per point
//     (deliberately dropped — the surface/groundwater split isn't always
//     available per sub-basin anyway, see AggregatedContribution's own
//     header, so a "just the total" column reads cleaner than a breakdown
//     that may not even add up to it).
//
// Both are bucketed at `granularity` (CalculationResults.tsx's own "Ver
// por" selector) via `groupByPeriod`, same as the on-screen chart
// (AggregatedContributionChart.tsx/ContributionChart.tsx already do this) —
// the download used to always dump one row per raw input-series date
// regardless of what granularity was showing on screen, which read as
// "always daily" for anyone who picked Mensual/Anual before downloading.
//
// Lives under src/engine/, not src/model/ (where it used to sit): every
// shape here (SubBasinResult, AggregatedContribution) is an
// ENGINE-computed result, not raw project data — keeping it here follows
// the same one-way model -> engine dependency direction as the rest of the
// codebase (see CLAUDE.md), instead of engine.ts's/aggregation.ts's own
// types.
// ---------------------------------------------------------------------------
import { groupByPeriod, type Granularity } from "../model/aggregates.ts";
import { isoToDdmmyyyy } from "../model/isoDate.ts";
import type { AggregatedContribution } from "./aggregation.ts";
import type { SubBasinResult } from "./engine.ts";

/** "period" (`groupByPeriod`'s own key: a raw ISO date for daily, "yyyy-mm" for monthly, "yyyy" for annual) -> the CSV's own date column, dd/mm/yyyy or mm/yyyy or yyyy to match. */
function formatPeriodForCsv(period: string, granularity: Granularity): string {
  if (granularity === "daily") return isoToDdmmyyyy(period);
  if (granularity === "monthly") {
    const [year, month] = period.split("-");
    return `${month}/${year}`;
  }
  return period;
}

/** Diagnostic CSV: "Fecha;<sub-basin>..." header + one row per `granularity` period, in mm (summed within each period — mm/day summed over a month IS that month's total mm, same reasoning groupByPeriod already uses for the on-screen chart). */
export function generateSubBasinContributionsCsv(
  dates: string[],
  results: SubBasinResult[],
  granularity: Granularity
): string {
  const bucketed = results.map((r) => groupByPeriod(dates, r.contribution, granularity));
  const periods = bucketed[0]?.map((b) => b.period) ?? [];
  const header = ["Fecha", ...results.map((r) => r.description)].join(";");
  const rows = periods.map((period, i) => {
    const values = bucketed.map((b) => b[i].totalMm.toFixed(4));
    return [formatPeriodForCsv(period, granularity), ...values].join(";");
  });
  return [header, ...rows].join("\r\n");
}

/**
 * CSV for an already-aggregated level (outlet point or zone — both share
 * the same AggregatedContribution shape, see aggregation.ts): header
 * "Fecha;<point>;<point>..." + one row per `granularity` period, in volume
 * (Hm³ with the coefficient in effect, summed within each period).
 */
export function generateAggregatedContributionsCsv(
  dates: string[],
  aggregated: (AggregatedContribution & { name: string })[],
  granularity: Granularity
): string {
  const bucketed = aggregated.map((a) => groupByPeriod(dates, a.volume, granularity));
  const periods = bucketed[0]?.map((b) => b.period) ?? [];
  const header = ["Fecha", ...aggregated.map((a) => a.name)].join(";");
  const rows = periods.map((period, i) => {
    const values = bucketed.map((b) => b[i].totalMm.toFixed(6));
    return [formatPeriodForCsv(period, granularity), ...values].join(";");
  });
  return [header, ...rows].join("\r\n");
}

/**
 * Builds the detail CSV (step-by-step internal state) of a single
 * sub-basin — the web equivalent of the real .exe's "Sal_parciales.csv",
 * with one column per `result.detail` series instead of one per sub-basin.
 */
export function generateDetailCsv(dates: string[], result: SubBasinResult): string {
  const seriesNames = Object.keys(result.detail ?? {});
  const header = ["Fecha", ...seriesNames].join(";");
  const rows = dates.map((date, i) => {
    const values = seriesNames.map((name) => result.detail![name][i].toFixed(4));
    return [isoToDdmmyyyy(date), ...values].join(";");
  });
  return [header, ...rows].join("\r\n");
}

/** Downloads text as a file in the browser (no extra dependencies). */
export function downloadText(fileName: string, content: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
